import { useState } from 'react';
import type { CameraAdapter } from '../data/camera';
import type { MicrophoneAdapter } from '../data/microphone';
import type { Class, ClassCaptureSettings, ProjectKind, Sample } from '../data/types';
import { ClassCard } from './ClassCard';
import './ClassList.css';

const MAX_CLASS_NAME_LENGTH = 40;

export interface ClassListProps {
  classes: Class[];
  /** Project kind drives image vs audio capture. Defaults to image. */
  kind?: ProjectKind;
  loading: boolean;
  error: string | null;
  /** Map of classId -> samples for that class. Empty array when not yet loaded. */
  samplesByClass: Record<string, { byId: Sample[]; loading: boolean; error: string | null }>;
  onAdd: (name: string) => void | Promise<void>;
  onRename: (id: string, name: string) => void | Promise<void>;
  onUpdateCaptureSettings?: (id: string, settings: ClassCaptureSettings) => void | Promise<void>;
  onToggleDisabled?: (id: string, disabled: boolean) => void | Promise<void>;
  onDelete: (id: string) => void;
  onRemoveAllSamples: (classId: string) => void;
  /** `opts.poseKeypoints` carries MoveNet keypoints from WebcamPanel
   *  pose-project captures so the store can persist them alongside
   *  the blob; dropping `opts` here breaks the skeleton overlay on
   *  saved thumbnails + the sample modal. */
  onAddSample: (
    classId: string,
    blob: Blob,
    opts?: { poseKeypoints?: ReadonlyArray<import('../data/types').SamplePoseKeypoint> },
  ) => void | Promise<void>;
  onDeleteSample: (classId: string, sampleId: string) => void | Promise<void>;
  /** Bubbles a thumbnail click up so the page can open the SampleModal. */
  onOpenSample?: (classId: string, sampleId: string) => void;
  /** For tests; production cards use the real browser camera adapter. */
  camera?: CameraAdapter;
  /** For tests; production cards use the real browser microphone adapter. */
  microphone?: MicrophoneAdapter;
}

export function ClassList({
  classes,
  kind = 'image',
  loading,
  error,
  samplesByClass,
  onAdd,
  onRename,
  onUpdateCaptureSettings,
  onToggleDisabled,
  onDelete,
  onRemoveAllSamples,
  onAddSample,
  onDeleteSample,
  onOpenSample,
  camera,
  microphone,
}: ClassListProps): JSX.Element {
  return (
    <section className="bl-classes" aria-label="Classes">
      <header className="bl-classes__header">
        <h2 className="bl-classes__title">Classes</h2>
        <p className="bl-classes__hint">
          A class is one thing the model can learn. For example: “me waving” or “a dog barking.”
        </p>
      </header>

      {error && (
        <p role="alert" className="bl-classes__error">
          {error}
        </p>
      )}

      {loading && classes.length === 0 ? (
        <p className="bl-classes__loading" aria-live="polite">
          Loading classes…
        </p>
      ) : (
        <ol className="bl-classes__list" aria-label="Class list">
          {classes.map((cls) => {
            const slice = samplesByClass[cls.id];
            return (
              <li key={cls.id}>
                <ClassCard
                  cls={cls}
                  kind={kind}
                  samples={slice?.byId ?? []}
                  samplesLoading={slice?.loading ?? false}
                  samplesError={slice?.error ?? null}
                  onRename={onRename}
                  {...(onUpdateCaptureSettings ? { onUpdateCaptureSettings } : {})}
                  {...(onToggleDisabled ? { onToggleDisabled } : {})}
                  onDelete={onDelete}
                  onRemoveAllSamples={onRemoveAllSamples}
                  onAddSample={onAddSample}
                  onDeleteSample={onDeleteSample}
                  {...(onOpenSample ? { onOpenSample } : {})}
                  {...(camera ? { camera } : {})}
                  {...(microphone ? { microphone } : {})}
                />
              </li>
            );
          })}
        </ol>
      )}

      <AddClassForm onAdd={onAdd} />
    </section>
  );
}

interface AddClassFormProps {
  onAdd: (name: string) => void | Promise<void>;
}

function AddClassForm({ onAdd }: AddClassFormProps): JSX.Element {
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const trimmed = name.trim();
  const canSubmit = trimmed.length > 0 && !submitting;

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await onAdd(trimmed);
      setName('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="bl-classes__add" onSubmit={submit}>
      <label className="bl-classes__add-label" htmlFor="bl-add-class">
        Add a class
      </label>
      <div className="bl-classes__add-row">
        <input
          id="bl-add-class"
          type="text"
          className="bl-classes__add-input"
          value={name}
          maxLength={MAX_CLASS_NAME_LENGTH}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. me waving"
        />
        <button type="submit" className="bl-classes__add-button" disabled={!canSubmit}>
          {submitting ? 'Adding…' : 'Add'}
        </button>
      </div>
    </form>
  );
}
